import React, { useMemo } from "react";
import "../../css/Sparkline.css";

/**
 * Sparkline — tiny dependency-free SVG area/line chart for a series of numbers
 * (e.g. upload throughput samples over time). Stretches to its container width;
 * the internal coordinate system is fixed (0..100 × 0..H) and scaled with
 * preserveAspectRatio="none" so we never recompute geometry on resize.
 *
 * Props:
 *   data     — number[] (>= 0). Fewer than 2 points renders a flat baseline.
 *   height   — px height of the rendered chart (default 48)
 *   variant  — 'accent' (default) | 'success' | 'warning' | 'danger'
 *   fill     — draw the area under the line (default true)
 */
const VARIANT_COLOR = {
    accent: "var(--qd-accent)",
    success: "var(--qd-success)",
    warning: "var(--qd-warning)",
    danger: "var(--qd-danger)",
};

const Sparkline = ({ data = [], height = 48, variant = "accent", fill = true, className = "", ariaLabel }) => {
    const H = 100; // internal vertical resolution
    const W = 100;
    const { line, area } = useMemo(() => {
        const pts = data.filter((v) => Number.isFinite(v) && v >= 0);
        if (pts.length < 2) {
            const y = H - 1;
            return { line: `M0,${y} L${W},${y}`, area: `M0,${y} L${W},${y} L${W},${H} L0,${H} Z` };
        }
        const max = Math.max(...pts, 1);
        const stepX = W / (pts.length - 1);
        const coords = pts.map((v, i) => {
            const x = i * stepX;
            // 4px top padding so a peak isn't clipped at the very edge
            const y = H - 4 - (v / max) * (H - 4);
            return [x, y];
        });
        const lineD = coords.map((p, i) => `${i === 0 ? "M" : "L"}${p[0].toFixed(2)},${p[1].toFixed(2)}`).join(" ");
        const areaD = `${lineD} L${W},${H} L0,${H} Z`;
        return { line: lineD, area: areaD };
    }, [data]);

    return (
        <svg
            className={`qd-spark ${className}`.trim()}
            viewBox={`0 0 ${W} ${H}`}
            preserveAspectRatio="none"
            style={{ height, color: VARIANT_COLOR[variant] || VARIANT_COLOR.accent }}
            role="img"
            aria-label={ariaLabel}
        >
            {fill && <path className="qd-spark-area" d={area} />}
            <path className="qd-spark-line" d={line} vectorEffect="non-scaling-stroke" />
        </svg>
    );
};

export default Sparkline;
